/**
 * Azure Web PubSub real-time service.
 *
 * Manages WebSocket connection to Azure Web PubSub, group subscriptions,
 * token refresh, and dispatches incoming messages as CustomEvents on
 * `window` using `pubnub:*` event names so that pubnub-legacy-bridge.ts
 * and usePubNubEvent.ts work unchanged.
 */

import { reauthorizeRealtime } from '@/services/api';
import {
  getAuthObject,
  setAuthObject,
  getCurrentUserId,
  getAvailableSubmodules,
} from '@/utils/auth';
import { hasValue } from '@/utils/validation';
import { isInArray } from '@/utils/arrays';
import { AccessAuthorities } from '@/models/access-authorities';
import { WebPubSubClient } from '@azure/web-pubsub-client';

let client: WebPubSubClient | null = null;
let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

// Counters for reload-burst telemetry (see reload-burst-telemetry.ts)
const diagnostics = {
  clientsCreated: 0,
  reauthorizes: 0,
  staleStopsIgnored: 0,
  staleMessagesDropped: 0,
};

export function getRealtimeDiagnostics(): typeof diagnostics {
  return { ...diagnostics };
}

async function reauthorize(): Promise<any> {
  diagnostics.reauthorizes++;
  return reauthorizeRealtime();
}

// ── Token refresh ────────────────────────────────────────────────────

function clearTokenRefresh(): void {
  if (tokenRefreshInterval !== null) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
}

function getTokenTtlMinutes(): number {
  try {
    const authObj = getAuthObject();
    if (!authObj?.realtimeUrl) return 9;
    const token = authObj.realtimeUrl.split('access_token=')[1];
    if (!token) return 9;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresMs = payload.exp * 1000;
    const remainingMs = expiresMs - Date.now();
    return Math.max(1, Math.floor(remainingMs / 60_000) - 1); // refresh 1 min before expiry
  } catch {
    return 9;
  }
}

function resetTokenRefresh(refreshRateMinutes?: number): void {
  clearTokenRefresh();
  const minutes = refreshRateMinutes ?? getTokenTtlMinutes();
  tokenRefreshInterval = setInterval(
    () => refreshAndReconnect(),
    minutes * 60_000,
  );
}

let pendingReconnect = false;

async function refreshAndReconnect(): Promise<void> {
  if (!getAuthObject()) return;

  if (!navigator.onLine) {
    // Offline — wait for the browser to come back online, then retry
    if (!pendingReconnect) {
      pendingReconnect = true;
      window.addEventListener('online', function onOnline() {
        window.removeEventListener('online', onOnline);
        pendingReconnect = false;
        refreshAndReconnect();
      });
    }
    return;
  }

  try {
    const result = await reauthorize();
    const authObj = getAuthObject();
    if (authObj && result) {
      authObj.realtimeToken = result.realtimeToken ?? result.pubNubToken;
      authObj.realtimeUrl = result.realtimeUrl;
      setAuthObject(authObj);
    }

    // Reconnect with new token. Clearing `client` before stop() makes the old
    // client's 'stopped' event stale, so it won't start a second reconnect.
    const old = client;
    client = null;
    if (old) {
      try { old.stop(); } catch { /* ignore */ }
    }
    initAzureWebPubSub();
  } catch {
    // Failed even though we're online — retry in 5s
    setTimeout(() => refreshAndReconnect(), 5_000);
  }
}

// ── Event dispatch (pubnub:* CustomEvent format) ─────────────────────

function dispatchEvent(pnmType: string, message: any, group: string): void {
  window.dispatchEvent(
    new CustomEvent(`pubnub:${pnmType}`, {
      detail: { ...message, _channel: group },
    }),
  );
}

// ── Group matching (hyphen-separated Azure group names) ──────────────

const officerAssignedRe = /^officer-\d+-assigned-callcard$/;
const ornaUserReadOnlyRe = /^ornauser-\d+-readonly$/;
const companyReadOnlyRe = /^company-\d+-readonly$/;
const companyReadWriteRe = /^company-\d+-readwrite$/;

function handleGroupMessage(group: string, data: any): void {
  const pnmType: string | undefined = data?.pnmType;
  if (!pnmType) return;

  // Dispatch generic event
  dispatchEvent(pnmType, data, group);

  // Dispatch channel-category event
  if (officerAssignedRe.test(group)) {
    dispatchEvent(`officer:${pnmType}`, data, group);
  } else if (ornaUserReadOnlyRe.test(group)) {
    dispatchEvent(`user:${pnmType}`, data, group);
  } else if (companyReadOnlyRe.test(group)) {
    dispatchEvent(`company:${pnmType}`, data, group);
  } else if (companyReadWriteRe.test(group)) {
    dispatchEvent(`companyRW:${pnmType}`, data, group);
  } else if (group === 'orna-readonly') {
    dispatchEvent(`orna:${pnmType}`, data, group);
  }
}

// ── Post-connection setup ─────────────────────────────────────────────

async function onConnected(target: WebPubSubClient, callback?: () => void, isRetry = false): Promise<void> {
  // Replaced while starting (e.g. a token refresh raced the initial connect);
  // the newer client runs its own setup.
  if (target !== client) {
    callback?.();
    return;
  }

  const authObj = getAuthObject();
  const userId = getCurrentUserId();
  const companyId = authObj?.company_id;

  const groups = [
    `officer-${userId}-assigned-callcard`,
    `ornauser-${userId}-readonly`,
    `company-${companyId}-readonly`,
    'orna-readonly',
  ];

  const submodules = getAvailableSubmodules() ?? [];
  if (isInArray(submodules, AccessAuthorities.ROLE_VIEW_MESSAGING)) {
    groups.push(`company-${companyId}-readwrite`);
  }

  let hadForbidden = false;
  for (const group of groups) {
    try {
      await target.joinGroup(group);
    } catch (err: any) {
      const isForbidden = err?.name === 'Forbidden' || err?.message?.includes('Forbidden');
      if (isForbidden) {
        hadForbidden = true;
      }
      console.warn(`Failed to join group ${group}:`, err);
    }
  }

  // If a group join was forbidden and this isn't already a retry,
  // reauthorize to get a fresh JWT with correct permissions and reconnect
  if (hadForbidden && !isRetry) {
    console.warn('Group join forbidden — reauthorizing and reconnecting...');
    try {
      const result = await reauthorize();
      if (result?.realtimeUrl) {
        const freshAuthObj = getAuthObject();
        if (freshAuthObj) {
          freshAuthObj.realtimeToken = result.realtimeToken ?? result.pubNubToken;
          freshAuthObj.realtimeUrl = result.realtimeUrl;
          setAuthObject(freshAuthObj);
        }
        const old = client;
        client = null;
        if (old) {
          try { old.stop(); } catch { /* ignore */ }
        }
        const fresh = createClient(result.realtimeUrl);
        await fresh.start();
        await onConnected(fresh, callback, true);
        return;
      }
    } catch (err) {
      console.error('Reauthorize after forbidden failed:', err);
    }
  }

  resetTokenRefresh();
  callback?.();
}

// ── Client creation ──────────────────────────────────────────────────

/**
 * Creates a client, makes it the current one, and wires its events.
 *
 * Every handler checks that its client is still the current one. A client
 * that was replaced (token refresh, forbidden retry) still emits 'stopped'
 * when we stop it; before this guard that event nulled the NEW client and
 * started yet another reconnect, leaving an orphaned client that stayed
 * connected and dispatched every message a second time. Long-open screens
 * collected one more orphan per token refresh.
 */
function createClient(url: string): WebPubSubClient {
  const created = new WebPubSubClient(url, { autoReconnect: true });
  client = created;
  diagnostics.clientsCreated++;

  created.on('group-message', (e: any) => {
    if (created !== client) {
      diagnostics.staleMessagesDropped++;
      return;
    }
    const group: string = e.message.group;
    const data = typeof e.message.data === 'string'
      ? JSON.parse(e.message.data)
      : e.message.data;
    handleGroupMessage(group, data);
  });

  created.on('connected', () => {
    if (created !== client) return;
    window.dispatchEvent(
      new CustomEvent('pubnub:presence', {
        detail: { action: 'join', uuid: 'user' + getCurrentUserId() },
      }),
    );
  });

  created.on('stopped', () => {
    if (created !== client) {
      // We stopped it on purpose, or it was already replaced
      diagnostics.staleStopsIgnored++;
      return;
    }
    // Connection stopped — auto-reconnect gave up (likely expired token).
    console.warn('Azure Web PubSub stopped — scheduling reauthorize and reconnect...');
    client = null;
    refreshAndReconnect();
  });

  return created;
}

// ── Public API ───────────────────────────────────────────────────────

export function initAzureWebPubSub(callback?: () => void): void {
  const authObj = getAuthObject();
  if (!hasValue(authObj) || !authObj.realtimeUrl) {
    callback?.();
    return;
  }

  // Already initialized
  if (client) {
    callback?.();
    return;
  }

  const initial = createClient(authObj.realtimeUrl);

  initial.start().then(() => {
    onConnected(initial, callback);
  }).catch(async (err) => {
    // Connection failed — likely expired token. Fetch fresh credentials and retry.
    console.warn('Azure Web PubSub initial connection failed, attempting reauthorize...', err);
    if (client === initial) client = null;
    try {
      const result = await reauthorize();
      if (result?.realtimeUrl) {
        const freshAuthObj = getAuthObject();
        if (freshAuthObj) {
          freshAuthObj.realtimeToken = result.realtimeToken ?? result.pubNubToken;
          freshAuthObj.realtimeUrl = result.realtimeUrl;
          setAuthObject(freshAuthObj);
        }
        const fresh = createClient(result.realtimeUrl);
        await fresh.start();
        onConnected(fresh, callback);
      } else {
        console.error('Azure Web PubSub reauthorize returned no URL');
        callback?.();
      }
    } catch (retryErr) {
      console.error('Azure Web PubSub retry failed:', retryErr);
      callback?.();
    }
  });
}

export function stopAzureWebPubSub(): void {
  clearTokenRefresh();
  const old = client;
  client = null;
  if (old) {
    try { old.stop(); } catch { /* ignore */ }
  }
}

export function isInitialized(): boolean {
  return client !== null;
}
